import { FramerButton } from "@/components/shared";
import { useForgotPasswordMutationHook } from "@/hooks/mutations";
import { ChangeEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "react-lazy-load-image-component/src/effects/opacity.css";
import { PreloginLayout, PreloginLayoutContent, PreloginLayoutFooter} from "@/layouts/PreloginLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { handleValidation } from "@/utils/form";
import { Button } from "@/components/ui/button";

function useCountdown(initialTime: number) {
  const [remainingTime, setRemainingTime] = useState(initialTime);

  useEffect(() => {
    if (remainingTime > 0) {
      const timerId = setTimeout(() => {
        setRemainingTime(remainingTime - 1);
      }, 1000);

      return () => clearTimeout(timerId);
    }
  }, [remainingTime]);

  return [remainingTime, setRemainingTime] as const;
}

function ForgotPasswordComponent() {
  const [loading, setLoading] = useState(false);
  const [hasErrors, setHasErrors] = useState(true);
  const [canSend, setCanSend] = useState(true);
  const [email, setEmail] = useState("");

  const [remainingTime, setRemainingTime] = useCountdown(0);

  const navigate = useNavigate();
  const { forgotPasswordMutation } = useForgotPasswordMutationHook();

  useEffect(() => {
    if (remainingTime === 0 && !canSend) {
      setCanSend(true);
    }
  }, [remainingTime, canSend]);

  function handleFormSubmit(event: React.FormEvent) {
    event.preventDefault();
    setCanSend(false);
    setRemainingTime(120); // 2 minutes

    // fire the mutation if can send and email valid
    if (canSend && email !== "") {
      setLoading(true);

      forgotPasswordMutation({ variables: { mail: email } })
        .finally(() => {
          setLoading(false);
        });
    }
  }

  function handleOnChange(event: ChangeEvent<HTMLInputElement>) {
    // get the updated user input
    const { value } = event.target;
    const isValid = handleValidation(value, "email");
    if(value === "") {
      setEmail("");
      setHasErrors(true);
    }
    else if(isValid) {
      setEmail(value);
      setHasErrors(false);
    }
    else {
      setHasErrors(true);
    }
  }

  return (
    <PreloginLayout>
      {/* Form Container */}
      <PreloginLayoutContent>
        {/* Header wrapper */}
        <div className="mb-4 dark:text-white">
          <p className="text-xl md:text-2xl font-semibold mb-1">Request Password Reset</p>
          <p className="text-sm md:text-base">Please provide the email address associated with your account below to receive a password reset link.</p>
        </div>
        <form
          className="flex flex-col gap-2"
          onSubmit={handleFormSubmit}
          id="loginForm"
          noValidate
        >
          <div>
            <Label htmlFor="mail" className="dark:text-white">
              Enter your email address
            </Label>
            <Input
              className={`text-gray-800 dark:text-white ${hasErrors ? "border-red-500 focus:border-red-500" : ""}`}
              required
              onChange={(event: ChangeEvent<HTMLInputElement>) => handleOnChange(event)}
              placeholder="Enter your email address"
              type="email"
              name="mail"
              autoFocus
            />
            {hasErrors && <p className="mt-1 text-sm text-red-600 dark:text-red-400">Email is invalid</p>}
          </div>
          {
            !canSend &&
              <span className="dark:text-white">
                Send again in&nbsp;
                <span className="font-semibold">
                  {Math.floor(remainingTime / 60)}:{(remainingTime % 60).toString().padStart(2, "0")}
                </span>
              </span>
          }

          <FramerButton
            className={`
              bg-mforange text-white w-full hover:bg-mforange/80
              ${loading ? "bg-mforange/50 cursor-wait" : ""}
              ${hasErrors || !canSend ? "cursor-not-allowed bg-gray-400" : ""}
            `}
            type={hasErrors ? "button" : "submit"}
            disabled={hasErrors || !canSend}
          >
            {
              loading ?
                <div className="flex justify-center items-center dark:text-white">
                  <div className="w-4 h-4 border-2 border-gray-400 border-t-white mr-2 rounded-full animate-spin" />
                  Sending
                </div>
                : <>Send Reset Link</>
            }
          </FramerButton>

          {/* Forgot password */}
          <p className="mt-4 text-sm md:text-base">
            <span onClick={() => {navigate("/login");}} className="text-blue-400 cursor-pointer">Back to Login</span>
          </p>
        </form>
      </PreloginLayoutContent>

      {/* Request demo */}
      <PreloginLayoutFooter>
        <h1 className="text-base md:text-lg font-semibold mb-2 dark:text-white">Don&apos;t have an account?</h1>
        <Button
          data-cy="requestDemoButton"
          onClick={() => {
            window.open("https://calendly.com/myron-finance/myron-demo", "_blank", "noopener,noreferrer");
          }}
          className="bg-mforange text-white hover:bg-mforange/70"
        >
          Request Demo
        </Button>
      </PreloginLayoutFooter>
    </PreloginLayout>
  );
}

export default ForgotPasswordComponent;
